import { useMemo } from 'react';

import { useLocale } from 'next-intl';

import { useConfig } from '@/components/providers/config-provider';
import { useTriggerContext } from '@/components/providers/trigger-context-provider';
import { Locale } from '@/i18n/locales';
import { getLocalizedValue } from '@/lib/utils/localization';

function findTriggerOverrideKey(
  triggerOverrides: Record<string, unknown>,
  trigger: string,
): string | undefined {
  return Object.keys(triggerOverrides)
    .filter(key => trigger.startsWith(key))
    .sort((a, b) => b.length - a.length)[0];
}

/**
 * Hook to get localized configuration value from translations.
 * When a trigger context is active and the tenant has a matching triggerOverride,
 * returns the override value if available; otherwise falls through to the default.
 */
export function useLocalizedConfig<T = unknown>(keyPath: string): T | null {
  const config = useConfig();
  const locale = useLocale() as Locale;
  const trigger = useTriggerContext();

  return useMemo(() => {
    if (trigger && config.triggerOverrides) {
      const overrideKey = findTriggerOverrideKey(config.triggerOverrides, trigger);
      if (overrideKey) {
        const override = config.triggerOverrides[overrideKey];
        const overrideValue = getLocalizedValue<T>(
          { ...config, translations: override.translations },
          locale,
          keyPath,
        );
        if (overrideValue !== null) return overrideValue;
      }
    }
    return getLocalizedValue<T>(config, locale, keyPath);
  }, [config, locale, keyPath, trigger]);
}
